// Imports for external libraries go here.
import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';

// separated by a blank line from external imports.
// The closer the import is to the file, the lower it should be in this list.
import { StyledNavigationContainer, StyledNavigationPlaceholder } from './NavigationContainer.styles';
import { NavigationContainerProps } from './NavigationContainer.types';
import { NavigationContext } from './NavigationContextProvider';
import { LanguageSelectorContext } from '../../molecules/Navigation/LanguageSelector/LanguageSelectorContextProvider';
import { MyTripsModal } from '../../molecules/Navigation/MyTrips';
import { NavigationWrapper, NavigationUtilityLinkWrapper, UtilityNavigationType } from '../../molecules/Navigation';
import { hotelLogoQuery } from './queries';
import { CACHE_MARRIOTT_URL, HOTEL_LOGO_ALT_TEXT } from '../../utils/constants';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useScrollStore, useStickyState, useGlobalStore } from '@marriott/mi-store-utils';
import { HeaderLogo, AEMModel, canUseDOM, Image } from '@marriott/mi-ui-library';

export const NavigationContainer: FC<NavigationContainerProps> = props => {
  const [iswhiteHeader, setIsWhiteHeader] = useState<boolean>(false);
  const [isMegaMenuActive, setIsMegaMenuActive] = useState<boolean>(false);
  const { model, cqPath, isAuthorMode } = props;
  const styleclass = model?.styleclass;
  const { logoUrl, logoImageAltText, logoType } = model?.resourceProperties ?? {};
  const primaryNavigationProps: AEMModel = model?.cqItems ? model.cqItems['primaryNavigation'] : {};
  const isTransparentHeader = model?.resourceProperties?.transparency === 'true';
  const utilityNavigation = (model?.cqItems ? model.cqItems['utilityNavigation'] : {}) as UtilityNavigationType;

  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const scrollPosition = useScrollStore(state => state.scrollPosition);
  const { isSticky, offset } = useStickyState(model.componentId);
  const { sessionData } = useGlobalStore(state => state);

  const isBrandedVariation = model.resourceProperties?.branded === 'true';

  // find the property id from the global store with fallback to datalayer window object
  const propertyId =
    (canUseDOM &&
      ((sessionData?.['cacheData']?.data?.AriesSearch?.searchCriteria?.propertyId ||
        window.dataLayer?.['prop_marsha_code'] ||
        window.dataLayer?.['mrshaCode']) as string | undefined)) ||
    'NYCES';

  const variables: { propertyId: string } = { propertyId: propertyId as string };

  // fetch hotel logo
  const { data } = useQuery(hotelLogoQuery, {
    context: {
      headers: {},
    },

    skip: !propertyId,
    variables,
    onError: error => console.error('Error fetching brand logo', error),
  });

  const hotelLogoUrl = data?.property?.media?.hotelLogo?.edges?.[0]?.node?.imageSrc ?? '';

  const navRef = useRef(null);

  const headerContainerRef = useCallback((ref: HTMLDivElement) => {
    if (!ref) {
      return;
    }
    setRegisteredComponent({
      componentId: model.componentId,
      isStickyOnScrollUp: !!model?.scrollSetting?.isStickyOnScrollUp,
      isStickyOnScrollDown: !!model?.scrollSetting?.isStickyOnScrollDown,
      height: ref.clientHeight,
    });
  }, []);

  useEffect(() => {
    setIsWhiteHeader(isMegaMenuActive && scrollPosition > 0);
  }, [scrollPosition, isMegaMenuActive]);

  const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState<boolean>(false);
  const [isMainMenuOpen, setIsMainMenuOpen] = useState<boolean>(false);
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState<boolean>(false);
  const [isMyTripsModalOpen, setIsMyTripsModalOpen] = useState<boolean>(false);

  const isInverse = (!isSticky && !iswhiteHeader && isTransparentHeader && !isMainMenuOpen) || isBrandedVariation;

  // ensure the Branch banner z-index is lowered when the main menu or sign in drawer is open
  useEffect(() => {
    const reset = () => {
      document.getElementById('branch-banner-iframe')?.style.removeProperty('z-index');
    };

    if (isMainMenuOpen || isSignInDrawerOpen) {
      document.getElementById('branch-banner-iframe')?.style.setProperty('z-index', '50');
    } else {
      reset();
    }

    return () => {
      reset();
    };
  }, [isMainMenuOpen, isSignInDrawerOpen]);

  return (
    <NavigationContext.Provider
      value={{
        offset,
        navRef,
        isInverse,
        isSignInDrawerOpen,
        setIsSignInDrawerOpen,
        isMainMenuOpen,
        setIsMainMenuOpen,
        isMyTripsModalOpen,
        setIsMyTripsModalOpen,
      }}
    >
      <LanguageSelectorContext.Provider value={{ isLanguageSelectorOpen, setIsLanguageSelectorOpen }}>
        <StyledNavigationContainer
          id="navcontainer"
          data-component-name="o-common-static-navigationcontainer"
          ref={headerContainerRef}
          isBrandedVariation={isBrandedVariation}
          onMouseOver={() => setIsWhiteHeader(true)}
          onFocus={() => {
            setIsWhiteHeader(true);
          }}
          onMouseOut={() => {
            if (!isMegaMenuActive && !isSignInDrawerOpen) {
              setIsWhiteHeader(false);
            }
          }}
          className={clsx(styleclass, {
            'default-background inverse': isInverse,
            'header-sticky t-background-color': isSticky,
            'header-transparent': isTransparentHeader,
            'header-main-menu-open': isMainMenuOpen,
            'standard t-background-color': (iswhiteHeader && !isSticky) || !isTransparentHeader || isMainMenuOpen,
            'is-overlay': isMegaMenuActive || isSignInDrawerOpen || isMainMenuOpen || isLanguageSelectorOpen,
            'brand-css': isBrandedVariation,
          })}
        >
          <header role="banner" ref={navRef}>
            <div
              className={clsx('banner-row row align-items-center mx-0', { 'color-scheme7 h-100': isBrandedVariation })}
            >
              <div
                className={clsx('navigation-container', {
                  'container-lg container-xl p-0': !styleclass?.includes('standard'),
                })}
              >
                <div
                  className={clsx('navigation-logo-container', {
                    'branded-logo-container': isBrandedVariation,
                  })}
                >
                  {!isBrandedVariation ? (
                    <HeaderLogo
                      logoUrl={logoUrl}
                      logoImageAltText={logoImageAltText}
                      logoType={logoType}
                      customClassName="ml-3 ml-lg-0"
                    />
                  ) : (
                    <Image
                      defaultImageURL={`${CACHE_MARRIOTT_URL}${hotelLogoUrl}`}
                      customClass={'hotel-logo'}
                      altText={HOTEL_LOGO_ALT_TEXT}
                    />
                  )}
                </div>
                <NavigationWrapper
                  isAuthorMode={isAuthorMode}
                  cqPath={cqPath}
                  model={primaryNavigationProps}
                  utilityNavigation={utilityNavigation}
                  setIsMegaMenuActive={setIsMegaMenuActive}
                  isBrandedVariation={isBrandedVariation}
                />
                {utilityNavigation?.[':items'] && (
                  <NavigationUtilityLinkWrapper
                    utilityNavigation={utilityNavigation}
                    isRenderedInsideOverlay={false}
                    isAuthorMode={isAuthorMode}
                    cqPath={cqPath}
                    logoUrl={logoUrl}
                    logoImageAltText={logoImageAltText}
                    logoType={logoType}
                    isBrandedVariation={isBrandedVariation}
                  />
                )}
              </div>
            </div>
          </header>
        </StyledNavigationContainer>
        {
          // When the absolutely positioned non-transparent header goes sticky,
          // we need to continue accounting for that space
          isSticky && !isTransparentHeader && <StyledNavigationPlaceholder />
        }
        {isMyTripsModalOpen && <MyTripsModal />}
      </LanguageSelectorContext.Provider>
    </NavigationContext.Provider>
  );
};
